import React, { useState, useEffect } from 'react';
import { formatDate } from '../lib/dateUtils';
import { db, auth, collection, query, where, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, setDoc, deleteDoc, orderBy, limit } from '../firebase';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, Folder, Clock, ChevronRight, Activity, Rocket, Zap, ArrowLeft, Users, Share2, MessageSquare, TrendingUp, ShieldCheck, ExternalLink, Megaphone, Anchor, AlertCircle, Palette, Brush, Trash2, Loader2, Eye, EyeOff, FileText, ShoppingBag, Edit3, X, Save } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { trackEvent } from '../lib/analytics';
import { createCheckoutSession } from '../lib/stripe';
import { trackUserAction, subscribeToUserEvents, UserEvent } from '../lib/events';
import { YachtLogo } from '../components/YachtLogo';

import { getPlanConfig, getUpgradeTrigger, UpgradeTriggerStore } from '../lib/pricing';
import { UpgradeModal } from '../components/UpgradeModal';
import { handleFirestoreError } from '../lib/firebaseUtils';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

const BuildList = ({ builds, projectId, onDelete, onEdit }: { builds: any[], projectId: string, onDelete: (id: string, name: string) => void, onEdit: (build: any) => void }) => {
  return (
    <div className="space-y-4">
      {builds.length === 0 ? (
        <div className="p-8 text-center bg-[#0A0A0A] border border-[#222222] border-dashed rounded-xl">
          <p className="text-[10px] font-mono text-[#555555] uppercase tracking-widest leading-relaxed">
            No builds found. <br/> Initialize a build in the labs to begin.
          </p>
        </div>
      ) : (
        builds.slice(0, 10).map((build) => (
          <div key={build.id} className="group relative bg-[#0A0A0A] border border-[#1A1A1A] rounded-2xl overflow-hidden hover:border-[#333] transition-all flex flex-col sm:flex-row">
            {/* Visual Preview */}
            <div className="w-full sm:w-48 aspect-video sm:aspect-square bg-[#050505] border-b sm:border-b-0 sm:border-r border-[#1A1A1A] relative overflow-hidden flex items-center justify-center shrink-0">
               {build.content && (
                 <iframe 
                   srcDoc={build.content}
                   className="w-[1000px] h-[1000px] scale-[0.15] sm:scale-[0.18] pointer-events-none origin-center opacity-40 group-hover:opacity-100 transition-opacity"
                   title="Build preview"
                 />
               )}
               <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent opacity-40" />
            </div>

            <div className="p-5 flex-1 flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-2">
                  <h4 className="text-sm text-white font-mono uppercase tracking-tight truncate pr-4">{build.title || 'Untitled Build'}</h4>
                  <div className="flex gap-1">
                    <button 
                      onClick={() => onEdit(build)}
                      className="p-1.5 bg-[#111] border border-[#222] rounded hover:bg-white hover:text-black transition-all"
                      title="Edit Metadata"
                    >
                      <Edit3 size={12} />
                    </button>
                    <button 
                      onClick={() => onDelete(build.id, build.title)}
                      className="p-1.5 bg-[#111] border border-[#222] rounded hover:bg-red-900/30 hover:border-red-900/50 hover:text-red-500 transition-all text-[#333]"
                      title="Purge Build"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                <p className="text-[10px] text-[#555] font-sans leading-relaxed line-clamp-2 mb-4">
                  {build.description || 'No description provided for this architectural synthesis.'}
                </p>
              </div>

              <div className="flex items-center justify-between mt-auto">
                <div className="flex items-center gap-2">
                  <p className="text-[9px] font-mono text-[#555] uppercase">{formatDate(build.createdAt)}</p>
                  {build.type === 'app_template' && (
                    <span className="text-[8px] px-1.5 py-0.5 bg-blue-900/40 text-blue-400 rounded border border-blue-500/20 uppercase font-mono">Template</span>
                  )}
                </div>
                <Link 
                  to={`/studio/build?project=${projectId}&build=${build.id}`}
                  className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-[#0074D9] hover:text-[#7FDBFF] transition-colors"
                >
                  Open Workbench <ExternalLink size={10} />
                </Link>
              </div>
            </div>
          </div>
        ))
      )}
      {builds.length > 10 && (
         <Link 
          to={`/studio/build?project=${projectId}`}
          className="block text-center p-3 bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl text-[9px] font-mono uppercase tracking-widest text-[#555] hover:text-[#888] transition-colors"
         >
          View all {builds.length} builds in Build Lab
         </Link>
      )}
    </div>
  );
};

export default function Studio({ profile }: { profile: any }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = searchParams.get('project');
  
  const [projects, setProjects] = useState<any[]>([]);
  const [currentProject, setCurrentProject] = useState<any>(null);
  const [builds, setBuilds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchStep, setLaunchStep] = useState(0);
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Edit State
  const [editingBuild, setEditingBuild] = useState<any>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const startEditing = (build: any) => {
    setEditingBuild(build);
    setEditTitle(build.title || '');
    setEditDesc(build.description || '');
  };

  const handleUpdateBuildMetadata = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBuild || !projectId || !auth.currentUser) return;

    setIsSaving(true);
    const toastId = toast.loading("Updating build identity...");
    try {
      await updateDoc(doc(db, `projects/${projectId}/documents`, editingBuild.id), {
        title: editTitle,
        description: editDesc,
        updatedAt: serverTimestamp()
      });
      toast.success("Build identity refined", { id: toastId });
      setEditingBuild(null);
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update build", { id: toastId });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpgrade = async (plan: string = 'pro') => {
    if (!auth.currentUser) {
      toast.error('Please sign in to upgrade.');
      return;
    }

    setIsUpgrading(true);
    try {
      await createCheckoutSession('', auth.currentUser.uid, plan);
    } catch (error: any) {
      toast.error('Checkout failed', {
        description: error.message || 'The checkout engine encountered a capacity error.'
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  const toggleVisibility = async () => {
    if (!currentProject || !auth.currentUser) return;
    const isPublic = currentProject.isPublic ?? true;
    const newState = !isPublic;
    
    const toastId = toast.loading("Updating visibility...");
    try {
      await updateDoc(doc(db, 'projects', currentProject.id), {
        isPublic: newState,
        updatedAt: serverTimestamp()
      });
      
      try {
        await updateDoc(doc(db, 'deployments', currentProject.id), {
          isPublic: newState
        });
      } catch (e) {
        console.warn("Deployment update skipped:", e);
      }
      
      toast.success(newState ? "Project published to gallery" : "Project hidden from gallery", { id: toastId });
    } catch (error) {
      console.error("Failed to toggle visibility:", error);
      toast.error("Failed to update visibility settings.", { id: toastId });
    }
  };

  useEffect(() => {
    if (!auth.currentUser) return;

    const q = query(
      collection(db, 'projects'),
      where('ownerId', '==', auth.currentUser.uid),
      orderBy('updatedAt', 'desc'),
      limit(50) // Optimization: limit background reads
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const p = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));
        const sortedProjects = p; // Already sorted by query
        setProjects(sortedProjects);
        
        if (projectId) {
          const found = p.find(proj => proj.id === projectId);
          setCurrentProject(found || null);
        } else if (sortedProjects.length > 0 && !projectId) {
          // Auto-select most recent project if none selected
          setSearchParams({ project: sortedProjects[0].id });
        } else {
          setCurrentProject(null);
        }
        
        setLoading(false);
      },
      (error) => {
        console.error("Studio Project Listener Error:", error);
        setLoading(false); // Stop loading even on error
        handleFirestoreError(error, 'watch', `projects?ownerId=${auth.currentUser?.uid}`);
        if (error instanceof Error && (error.message.includes('permission') || (error as any).code === 'permission-denied')) {
          toast.error("Access denied to projects.");
        }
      }
    );

    return unsubscribe;
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !auth.currentUser) {
      setBuilds([]);
      return;
    }

    const q = query(
      collection(db, `projects/${projectId}/documents`),
      orderBy('createdAt', 'desc'),
      limit(20) // Optimization: limit background reads
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const b = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBuilds(b);
    }, (error) => {
      console.error("Studio Builds Listener Error:", error);
    });

    return unsubscribe;
  }, [projectId]);

  useEffect(() => {
    if (searchParams.get('upgrade') === 'true' && !profile?.isPro) {
      handleUpgrade();
      // Remove the param after triggering
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('upgrade');
      setSearchParams(newParams);
    }
  }, [searchParams, profile?.isPro]);

  const [activeVisitors, setActiveVisitors] = useState(0);
  const [userEvents, setUserEvents] = useState<UserEvent[]>([]);

  useEffect(() => {
    if (!auth.currentUser) return;
    const unsubscribe = subscribeToUserEvents(auth.currentUser.uid, (events) => {
      setUserEvents(events);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (currentProject?.isLaunched) {
      const interval = setInterval(() => {
        setActiveVisitors(prev => {
          const change = Math.floor(Math.random() * 3) - 1; // -1, 0, 1, or 2
          return Math.max(0, prev + change);
        });
      }, 5000);
      
      // Initial burst
      setActiveVisitors(Math.floor(Math.random() * 10) + 5);
      
      return () => clearInterval(interval);
    }
  }, [currentProject?.isLaunched]);

  const [upgradeTrigger, setUpgradeTrigger] = useState<UpgradeTriggerStore>(null);

  const plan = getPlanConfig(profile?.plan);
  const usage = {
    generationsUsed: profile?.generationsUsed || 0,
    activeProjects: profile?.projectCount || 0,
    hasAttemptedDeploy: false
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectName || !auth.currentUser) return;

    // Check project creation limit
    const trigger = getUpgradeTrigger(plan.id, usage, 'create_project');
    if (trigger) {
      setUpgradeTrigger(trigger);
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'projects'), {
        name: newProjectName,
        ownerId: auth.currentUser.uid,
        isLaunched: false,
        autopilot: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setNewProjectName('');
      setShowNewProject(false);
      
      trackUserAction(auth.currentUser.uid, 'project_created', `Initialized project: ${newProjectName}`, 'System', docRef.id);
      
      toast.success('Project Initialized.', {
        description: "Nice work. You’ve officially moved faster than 90% of your competition today. Keep that momentum.",
        duration: 5000,
      });

      setSearchParams({ project: docRef.id });
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  const handleDeleteBuild = async (id: string, name: string) => {
    if (!projectId || !auth.currentUser) return;
    if (!window.confirm(`Are you sure you want to delete build "${name}"?`)) return;

    const toastId = toast.loading(`Deleting build...`);
    try {
      await deleteDoc(doc(db, `projects/${projectId}/documents`, id));
      toast.success("Build deleted", { id: toastId });
    } catch (error) {
      console.error("Delete build failed:", error);
      toast.error("Failed to delete build", { id: toastId });
    }
  };

  const handleLaunch = async () => {
    if (!currentProject || !auth.currentUser) return;

    // Check deploy permission
    const trigger = getUpgradeTrigger(plan.id, usage, 'deploy');
    if (trigger) {
      setUpgradeTrigger(trigger);
      return;
    }

    setIsLaunching(true);
    setLaunchStep(1);

    trackUserAction(auth.currentUser.uid, 'launch_sequence_started', `Ignition sequence started for ${currentProject.name}`, 'Ship', currentProject.id);

    // Step 1: The Cargo Prep (Synthesis)
    const brandedUrl = `${window.location.origin}/pub/${currentProject.id}`;
    const launchPackage = {
      id: currentProject.id,
      name: currentProject.name,
      url: brandedUrl,
      description: "A high-performance SaaS built with Yacht Labs.",
      timestamp: new Date().toISOString()
    };
    console.log("Cargo Prep Complete:", launchPackage);

    // Step 2: The Pitch (Inference)
    // In a real app, we'd call Gemini here. For the demo, we'll simulate the AI generation.
    const pitches = {
      x: `I just launched ${currentProject.name} using @YachtLabs. It solves the manual build problem in under 5 minutes. Check it out: ${brandedUrl}`,
      linkedin: `Excited to announce the launch of ${currentProject.name}. Built entirely on the Yacht Labs platform, this project represents a new standard in rapid development. View the live build: ${brandedUrl}`,
      indiehackers: `Just shipped ${currentProject.name}. Technical stack: React, Tailwind, Firebase. Built in record time using Yacht Labs' synthesis engine. Feedback welcome! ${brandedUrl}`
    };

    const steps = [
      "Cargo Prep: Bundling Launch Package...",
      "The Pitch: Generating AI Social Copy...",
      "The Delivery: Triggering Webhook Relay...",
      "Directory Submission: Pre-filling Product Hunt...",
      "Initializing Acoustics Radar...",
      "Mission Control Online."
    ];

    for (let i = 0; i < steps.length; i++) {
      setLaunchStep(i + 1);
      await new Promise(r => setTimeout(r, 1200));
      
      if (i === 2) {
        // Step 3: The Delivery (Kinetics)
        console.log("Webhook Triggered to Zapier/Make:", launchPackage);
      }
      
      if (i === 3) {
        // Step 4: The Directory Submission
        const phUrl = `https://www.producthunt.com/posts/new?url=${encodeURIComponent(launchPackage.url)}&name=${encodeURIComponent(currentProject.name)}&tagline=${encodeURIComponent(launchPackage.description)}`;
        window.open(phUrl, '_blank');
      }
    }

    try {
      // Create a deployment record for the project so the URL is live
      try {
        await setDoc(doc(db, 'deployments', currentProject.id), {
          projectId: currentProject.id,
          ownerId: auth.currentUser?.uid,
          ownerPlan: profile?.plan || 'free',
          title: currentProject.name,
          content: `Project ${currentProject.name} has been launched. Visit the Build section to generate detailed assets.`,
          status: 'Live',
          region: 'Global Edge',
          deployedAt: serverTimestamp(),
          url: brandedUrl,
          liveUrl: `${window.location.origin}/pub/${currentProject.id}`,
          type: 'main',
          isPublic: true
        });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, `deployments/${currentProject.id}`);
      }

      await updateDoc(doc(db, 'projects', currentProject.id), {
        isLaunched: true,
        isPublic: true,
        updatedAt: serverTimestamp()
      });

      trackEvent('project_launch', { projectId: currentProject.id });

      trackUserAction(auth.currentUser.uid, 'launch_successful', `Project ${currentProject.name} is now live`, 'Ship', currentProject.id);

      toast.success("Launch Successful", {
        description: "Your project is now live. Social posts are queued and Product Hunt is ready for submission."
      });
    } catch (error) {
      console.error("Launch failed:", error);
      toast.error("Launch Aborted", { description: "System error during ignition." });
    } finally {
      setIsLaunching(false);
      setLaunchStep(0);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#050505]">
        <motion.div 
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <YachtLogo className="w-12 h-12" />
        </motion.div>
      </div>
    );
  }

  // Mission Control View (Launched Project)
  if (currentProject?.isLaunched) {
    return (
      <div className="p-8 max-w-7xl mx-auto min-h-full flex flex-col bg-[#050505]">
        <Toaster position="top-right" theme="dark" />
        <UpgradeModal 
          isOpen={!!upgradeTrigger} 
          onClose={() => setUpgradeTrigger(null)} 
          onUpgrade={handleUpgrade}
          trigger={upgradeTrigger}
          appPreview={currentProject?.id ? `Building ${currentProject.name}...` : undefined}
        />
        
        <header className="flex justify-between items-start mb-12">
          <div className="flex items-center gap-6">
            <button 
              onClick={() => setSearchParams({})}
              className="w-10 h-10 bg-[#111111] border border-[#222222] rounded-lg flex items-center justify-center hover:bg-[#1A1A1A] hover:border-[#333333] transition-colors"
            >
              <ArrowLeft size={18} className="text-[#888888]" />
            </button>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl font-mono uppercase tracking-tighter text-white">{currentProject.name}</h1>
                <span className="px-2 py-0.5 bg-[#001F3F] text-[#0074D9] text-[10px] font-mono uppercase tracking-widest rounded border border-[#0074D9]/30">Live</span>
                <button 
                  onClick={toggleVisibility}
                  className={`flex items-center gap-2 px-2 py-0.5 rounded border text-[10px] font-mono uppercase tracking-widest transition-all ${
                    (currentProject.isPublic ?? true)
                      ? 'bg-emerald-950/30 text-emerald-500 border-emerald-900/50 hover:bg-emerald-900/40' 
                      : 'bg-zinc-900 text-zinc-500 border-zinc-800 hover:text-zinc-300'
                  }`}
                  title={(currentProject.isPublic ?? true) ? "Publicly listed in community gallery" : "Hidden from community gallery"}
                >
                  {(currentProject.isPublic ?? true) ? <Eye size={12} /> : <EyeOff size={12} />}
                  {(currentProject.isPublic ?? true) ? 'Public' : 'Private'}
                </button>
                {profile?.isPro && <div className="founder-badge">Plank Owner</div>}
              </div>
              <p className="text-[#888888] font-mono text-xs uppercase tracking-widest">Mission Control — Distribution & Growth Active</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Link 
              to={`/studio/think?project=${currentProject.id}`}
              className="tech-button-secondary"
            >
              Back to Workbench
            </Link>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-12">
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="tech-panel p-8 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <TrendingUp size={80} className="text-white" />
              </div>
              <p className="tech-label mb-2">Velocity</p>
              <p className="text-4xl font-mono text-white mb-1">{activeVisitors}</p>
              <p className="text-[10px] font-mono text-green-500 uppercase tracking-widest flex items-center gap-1">
                <Activity size={10} /> Active Visitors
              </p>
            </div>
            <div className="tech-panel p-8 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <MessageSquare size={80} className="text-white" />
              </div>
              <p className="tech-label mb-2">Resonance</p>
              <p className="text-4xl font-mono text-white mb-1">48</p>
              <p className="text-[10px] font-mono text-[#0074D9] uppercase tracking-widest">Social Mentions</p>
            </div>
            <div className="tech-panel p-8 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <Users size={80} className="text-white" />
              </div>
              <p className="tech-label mb-2">Conversion</p>
              <p className="text-4xl font-mono text-white mb-1">3 <span className="text-lg text-[#555555]">/ 50</span></p>
              <p className="text-[10px] font-mono text-purple-500 uppercase tracking-widest">Early Adopters</p>
            </div>
          </div>
          
          <div className="tech-panel p-8 flex flex-col justify-between">
            <div>
              <h3 className="tech-label text-white mb-4 flex items-center gap-2">
                <Zap size={14} className="text-yellow-500" /> Autopilot Status
              </h3>
              <p className="text-xs text-[#888888] leading-relaxed mb-6 font-sans">
                The Autopilot is currently promoting your launch on X and LinkedIn. 10 high-potential leads have been contacted.
              </p>
            </div>
            <Link 
              to={`/studio/repeat?project=${currentProject.id}`}
              className="text-[10px] font-mono uppercase tracking-widest text-[#555555] hover:text-white transition-colors flex items-center gap-2"
            >
              Manage Automation <ChevronRight size={12} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <section>
            <h2 className="tech-label mb-6">Build History</h2>
            <BuildList builds={builds} projectId={currentProject.id} onDelete={handleDeleteBuild} onEdit={startEditing} />
          </section>

          <section>
            <h2 className="tech-label mb-6">Live Distribution Log</h2>
            <div className="space-y-4">
              {userEvents.length === 0 ? (
                <div className="p-8 text-center bg-[#0A0A0A] border border-[#222222] border-dashed rounded-xl">
                  <p className="text-[10px] font-mono text-[#555555] uppercase tracking-widest leading-relaxed">
                    No recent telemetry detected. <br/> Start a process to initialize logging.
                  </p>
                </div>
              ) : (
                userEvents.map((log, i) => (
                  <div key={log.id || i} className="p-4 bg-[#0A0A0A] border border-[#222222] rounded-xl flex justify-between items-center group">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 bg-[#111111] border border-[#333333] rounded-lg flex items-center justify-center text-[10px] font-mono text-[#888888]">
                        {log.platform[0]}
                      </div>
                      <div>
                        <p className="text-sm text-white font-sans line-clamp-2 max-w-md">{log.description}</p>
                        <p className="text-[10px] font-mono text-[#555555] uppercase">{log.platform}</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-[#555555]">{log.timestamp ? formatDate(log.timestamp) : 'Just now'}</span>
                  </div>
                ))
              )}
            </div>
          </section>

          <section>
            <h2 className="tech-label mb-6">Plan Status</h2>
            <div className={`tech-panel p-8 ${plan.id !== 'free' ? 'border-[#0074D9]/50 bg-[#001F3F]/20' : 'border-[#333333] border-dashed'}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${plan.id !== 'free' ? 'bg-[#001F3F] text-[#0074D9] border-[#0074D9]/30' : 'bg-[#111111] text-[#888888] border-[#333333]'}`}>
                  {plan.id === 'pro' ? <Anchor size={24} /> : plan.id === 'founder' ? <Rocket size={24} /> : <ShieldCheck size={24} />}
                </div>
                <div>
                  <h3 className="text-lg font-mono tracking-tighter uppercase text-white">
                    {plan.name} Plan Active
                  </h3>
                  <p className="text-xs text-[#888888] font-sans">
                    {plan.id === 'free' ? `Generation limit: ${usage.generationsUsed}/${plan.generationLimit}` : 'Unlimited synthesis capacity and priority support active.'}
                  </p>
                </div>
              </div>
              
              {plan.id === 'free' && (
                <button
                  onClick={() => setUpgradeTrigger('deploy_block')}
                  className="w-full py-3 bg-[#0074D9] text-white text-[10px] font-mono uppercase tracking-widest rounded-xl hover:bg-[#0088ff] transition-colors"
                >
                  Upgrade to Ship
                </button>
              )}
            </div>
          </section>
        </div>
      </div>
    );
  }

  // Workbench View (Project Selected but not Launched)
  if (currentProject) {
    return (
      <div className="p-8 max-w-7xl mx-auto min-h-full flex flex-col bg-[#050505]">
        <Toaster position="top-right" theme="dark" />
        
        <header className="flex justify-between items-start mb-12">
          <div className="flex items-center gap-6">
            <button 
              onClick={() => setSearchParams({})}
              className="w-10 h-10 bg-[#111111] border border-[#222222] rounded-lg flex items-center justify-center hover:bg-[#1A1A1A] hover:border-[#333333] transition-colors"
            >
              <ArrowLeft size={18} className="text-[#888888]" />
            </button>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl font-mono uppercase tracking-tighter text-white">{currentProject.name}</h1>
                {profile?.isPro && <div className="founder-badge">Plank Owner</div>}
              </div>
              <p className="text-[#888888] font-mono text-xs uppercase tracking-widest">Workbench — Build & Refine</p>
            </div>
          </div>
          <div className="flex gap-4">
            <button 
              id="launch-button"
              onClick={() => {
                if (profile?.isPro) {
                  handleLaunch();
                } else {
                  toast.error("Pro Access Required", {
                    description: "The 'Launch' engine is reserved for Founder members. Claim your berth to unlock production distribution.",
                    action: {
                      label: "Upgrade",
                      onClick: () => setSearchParams({ project: currentProject.id, upgrade: 'true' })
                    }
                  });
                }
              }}
              disabled={isLaunching}
              className={`flex items-center gap-2 px-8 py-4 rounded-lg font-mono uppercase text-[11px] font-bold tracking-widest transition-all ${
                profile?.isPro 
                  ? 'bg-[#0074D9] text-white hover:bg-[#0056b3]' 
                  : 'bg-[#111111] border border-[#222222] text-[#555555] cursor-not-allowed'
              }`}
            >
              {isLaunching ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Rocket size={16} />
              )}
              {profile?.isPro ? 'Launch' : 'Locked'}
            </button>
          </div>
        </header>

        <AnimatePresence>
          {isLaunching && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed inset-0 z-[100] bg-[#050505] flex flex-col items-center justify-center p-12 text-center"
            >
              <motion.div
                animate={{ 
                  y: [0, -20, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-32 h-32 bg-[#001F3F] border border-[#0074D9]/30 rounded-full flex items-center justify-center mb-12 shadow-[0_0_100px_rgba(0,116,217,0.2)]"
              >
                <Rocket size={48} className="text-[#0074D9]" />
              </motion.div>
              
              <h2 className="text-4xl font-mono uppercase tracking-tighter mb-4 text-white">Ignition Sequence</h2>
              <div className="w-64 h-1 bg-[#222222] rounded-full mb-8 overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(launchStep / 6) * 100}%` }}
                  className="h-full bg-[#0074D9]"
                />
              </div>
              
              <p className="text-sm font-mono text-[#888888] uppercase tracking-widest h-8">
                {launchStep === 1 && "Igniting Distribution Megaphone..."}
                {launchStep === 2 && "Blasting to X & LinkedIn..."}
                {launchStep === 3 && "Submitting to Product Hunt..."}
                {launchStep === 4 && "Triggering Hunter Outreach..."}
                {launchStep === 5 && "Initializing Acoustics Radar..."}
                {launchStep === 6 && "Mission Control Online."}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'Inference', path: '/studio/think', icon: Zap, desc: 'Identify pain signals & leads' },
                { label: 'Synthesis', path: '/studio/build', icon: Folder, desc: 'Generate assets & bait' },
                { label: 'Kinetics', path: '/studio/ship', icon: Rocket, desc: 'Deploy technical build' },
                { label: 'Edit', path: '/studio/edit', icon: Palette, desc: 'Visual drag & drop builder' },
                { label: 'Marketplace', path: '/studio/marketplace', icon: ShoppingBag, desc: 'Acquire premium blueprints' },
                { label: 'Design', path: '/studio/design', icon: Brush, desc: 'Develop core design system' },
              ].map((lab) => (
                <Link 
                  key={lab.label}
                  to={`${lab.path}?project=${currentProject.id}`}
                  className="tech-panel p-6 hover:border-[#333333] hover:bg-[#0A0A0A] transition-all group"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 bg-[#111111] border border-[#222222] rounded-lg flex items-center justify-center group-hover:border-[#333333] transition-colors">
                      <lab.icon size={18} className="text-[#888888] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-lg font-mono uppercase tracking-tighter text-white">{lab.label}</h3>
                  </div>
                  <p className="text-xs text-zinc-500">{lab.desc}</p>
                </Link>
              ))}
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-3xl">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-100">Synthesis Gallery</h2>
                <FileText size={16} className="text-zinc-500" />
              </div>
              <BuildList builds={builds} projectId={currentProject.id} onDelete={handleDeleteBuild} onEdit={startEditing} />
            </section>

            <section className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-3xl">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-100">Distribution Strategy</h2>
                <Megaphone size={16} className="text-zinc-500" />
              </div>
              <div className="space-y-6">
                <div className="flex gap-6">
                  <div className="w-1 h-auto bg-blue-600 rounded-full" />
                  <div>
                    <h4 className="text-sm font-medium text-zinc-200 mb-1">Multi-Channel Blast</h4>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      Automatically announce your launch to X, LinkedIn, and Product Hunt. No manual copying required.
                    </p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="w-1 h-auto bg-purple-600 rounded-full" />
                  <div>
                    <h4 className="text-sm font-medium text-zinc-200 mb-1">Hunter Outreach</h4>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      Directly message the top 10 leads identified in the Inference stage with personalized invites.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="space-y-8">
            <section className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-3xl">
              <h2 className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 mb-6">Project Metadata</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] font-mono uppercase text-zinc-600 mb-1">Created</p>
                  <p className="text-sm text-zinc-300">{formatDate(currentProject.createdAt)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase text-zinc-600 mb-1">Owner</p>
                  <p className="text-sm text-zinc-300">{auth.currentUser?.email}</p>
                </div>
                <div className="pt-4 border-t border-zinc-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono uppercase text-zinc-600">Build Status</span>
                    <span className="text-[10px] font-mono text-green-500 uppercase">Ready</span>
                  </div>
                  <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="w-full h-full bg-green-500" />
                  </div>
                </div>
              </div>
            </section>

            <div className="p-8 bg-blue-600 rounded-3xl text-white relative overflow-hidden group cursor-pointer">
              <div className="absolute top-0 right-0 p-4 opacity-20 group-hover:scale-110 transition-transform">
                <Rocket size={80} />
              </div>
              <h3 className="text-xl font-mono uppercase mb-2">Ready for Takeoff?</h3>
              <p className="text-sm text-blue-100 mb-6 leading-relaxed">
                Hitting Launch will activate your distribution engine and start acquiring your first 50 users.
              </p>
              <button 
                onClick={() => {
                  if (profile?.isPro) {
                    handleLaunch();
                  } else {
                    toast.error("Pro Access Required", {
                      description: "The 'Launch' engine is reserved for Founder members. Claim your berth to unlock production distribution.",
                      action: {
                        label: "Upgrade",
                        onClick: () => setSearchParams({ project: currentProject.id, upgrade: 'true' })
                      }
                    });
                  }
                }}
                className={`w-full py-3 rounded-xl font-mono uppercase text-xs tracking-widest font-bold transition-all ${
                  profile?.isPro ? 'bg-white text-blue-600 hover:bg-blue-50' : 'bg-blue-700 text-blue-300 cursor-not-allowed'
                }`}
              >
                {profile?.isPro ? 'Ignite Now' : 'Locked'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDeleteProject = async (e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation();
    if (!auth.currentUser) return;

    const toastId = toast.loading(`Deleting project ${name}...`);
    try {
      await deleteDoc(doc(db, 'projects', id));
      
      // We should also delete subcollections, but in client side we just delete the root doc
      // Ideally a cloud function would clean up. 
      // For this demo, let's at least clear the deployments manually if we had a way.
      
      trackUserAction(auth.currentUser.uid, 'project_deleted', `Deleted project: ${name}`, 'System');
      toast.success("Project purged", { id: toastId });
      if (projectId === id) setSearchParams({});
    } catch (error) {
      console.error("Delete project failed:", error);
      toast.error("Failed to delete project", { id: toastId });
    }
  };

  // Project List View (Default)
  return (
    <div className="p-8 max-w-7xl mx-auto min-h-screen flex flex-col bg-zinc-950">
      <Toaster position="top-right" theme="dark" />
      <UpgradeModal 
        isOpen={!!upgradeTrigger} 
        onClose={() => setUpgradeTrigger(null)} 
        onUpgrade={handleUpgrade}
        trigger={upgradeTrigger}
      />
      
      <header className="flex justify-between items-end mb-12">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-4xl font-mono uppercase tracking-tighter">The Workbench</h1>
            {profile?.isPro && <div className="founder-badge">Plank Owner</div>}
          </div>
          <p className="text-zinc-500 font-sans text-sm">Welcome back, {auth.currentUser?.displayName}</p>
        </div>
        <div className="flex gap-4">
          {!profile?.isPro && (
            <button 
              onClick={() => handleUpgrade('founder')}
              disabled={isUpgrading}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600/10 text-blue-500 border border-blue-600/20 rounded-lg font-mono uppercase text-xs tracking-widest hover:bg-blue-600/20 transition-all disabled:opacity-50"
            >
              {isUpgrading ? <Loader2 className="animate-spin" size={14} /> : null}
              {isUpgrading ? 'Synthesizing Session...' : 'Claim Founder Status ($70 Off)'}
            </button>
          )}
          <button 
            onClick={() => setShowNewProject(true)}
            className="flex items-center gap-2 px-6 py-3 bg-zinc-100 text-zinc-950 rounded-lg font-mono uppercase text-xs tracking-widest hover:bg-zinc-300 transition-all"
          >
            <Plus size={16} /> New Build
          </button>
        </div>
      </header>

      {projects.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 p-8 bg-zinc-900/50 border border-zinc-800 rounded-[32px]"
          >
            <Zap size={48} className="text-yellow-400 mb-6 mx-auto" />
            <h2 className="text-3xl font-mono uppercase tracking-tighter mb-4 text-zinc-100">Welcome to the Lab.</h2>
            <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
              Your workspace is clean and ready for takeoff. Don't worry about making it perfect—just make it work.
            </p>
            <div className="space-y-4">
              <button 
                onClick={() => setShowNewProject(true)}
                className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-white text-black rounded-2xl text-lg font-bold uppercase tracking-widest hover:scale-[1.02] transition-all"
              >
                <Plus size={24} /> Initialize Your First Build
              </button>
            </div>
          </motion.div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { label: 'Active Builds', value: projects.length, icon: Rocket },
              { label: 'Think Tasks', value: '12', icon: Activity },
              { label: 'Studio Hours', value: '124h', icon: Clock },
            ].map((stat, i) => (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-2xl">
                <stat.icon className="text-zinc-500 mb-4" size={20} />
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">{stat.label}</p>
                <p className="text-3xl font-mono text-zinc-100">{stat.value}</p>
              </div>
            ))}
          </div>

          <section>
            <h2 className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 mb-6 font-bold">The Build Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <div 
                  key={project.id} 
                  className="relative group flex flex-col h-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-[24px] overflow-hidden hover:border-[#333] transition-all"
                >
                  {/* Build Preview Placeholder */}
                  <div className="aspect-[16/10] bg-[#050505] relative overflow-hidden flex items-center justify-center group-hover:bg-[#080808] transition-colors border-b border-[#1A1A1A]">
                    <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#333_1px,transparent_1px)] [background-size:20px_20px]" />
                    {project.isLaunched ? (
                      <Rocket size={40} className="text-[#0074D9] opacity-20 group-hover:scale-110 transition-transform duration-500" />
                    ) : (
                      <Zap size={40} className="text-[#222] group-hover:text-[#333] transition-colors" />
                    )}
                    <div className="absolute top-4 right-4 flex gap-2">
                       {project.isLaunched && (
                        <span className="px-2 py-0.5 bg-blue-900/30 text-blue-400 text-[8px] font-mono uppercase rounded border border-blue-500/20 backdrop-blur-md">Active</span>
                      )}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent opacity-60" />
                  </div>

                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-mono uppercase tracking-tighter text-white truncate pr-4">{project.name}</h3>
                      <button
                        onClick={(e) => handleDeleteProject(e, project.id, project.name)}
                        className="p-1 opacity-0 group-hover:opacity-100 text-[#333] hover:text-red-500 transition-all"
                        title="Purge Build"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    
                    <div className="mb-6 space-y-2">
                       <p className="text-[10px] text-[#555] font-mono uppercase tracking-widest flex items-center gap-2">
                        <Clock size={10} /> {formatDate(project.updatedAt)}
                      </p>
                      <p className="text-[10px] text-[#666] font-sans leading-relaxed line-clamp-2">
                        {project.isLaunched ? 'Production deployment live at edge. Growth tracking active.' : 'Awaiting synthesis. Workbench initialized and ready.'}
                      </p>
                    </div>

                    <button 
                      onClick={() => setSearchParams({ project: project.id })}
                      className="mt-auto w-full py-3 bg-[#111] border border-[#222] text-white text-[10px] font-mono uppercase tracking-widest rounded-xl hover:bg-white hover:text-black transition-all flex items-center justify-center gap-2 group/btn"
                    >
                      Enter Workbench <ChevronRight size={12} className="group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {showNewProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-zinc-800 p-8 rounded-3xl w-full max-w-md"
          >
            <h2 className="text-xl font-mono uppercase mb-6">Initialize Build</h2>
            <form onSubmit={handleCreateProject}>
              <input 
                autoFocus
                type="text" 
                placeholder="Build Name (e.g. Project Orchid)"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-zinc-100 mb-6 focus:outline-none focus:border-zinc-600 transition-colors placeholder:text-zinc-700 font-mono uppercase text-xs tracking-widest"
              />
              <div className="flex gap-4">
                <button 
                  type="button"
                  onClick={() => setShowNewProject(false)}
                  className="flex-1 py-3 border border-zinc-800 rounded-xl font-mono uppercase text-xs tracking-widest hover:bg-zinc-800 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 py-3 bg-zinc-100 text-zinc-950 rounded-xl font-mono uppercase text-xs tracking-widest hover:bg-zinc-300 transition-colors"
                >
                  Create
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {editingBuild && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0A0A0A] border border-[#222] p-8 rounded-3xl w-full max-w-lg shadow-[0_0_100px_rgba(255,255,255,0.05)]"
          >
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-xl font-mono uppercase tracking-tighter text-white">Refine Build Identity</h2>
              <button 
                onClick={() => setEditingBuild(null)}
                className="w-10 h-10 bg-[#111] border border-[#222] rounded-lg flex items-center justify-center hover:bg-[#1A1A1A] transition-colors"
              >
                <X size={18} className="text-[#888]" />
              </button>
            </div>

            <form onSubmit={handleUpdateBuildMetadata} className="space-y-6">
              <div>
                <label className="tech-label mb-2 block text-[#555]">Deployment Title</label>
                <input 
                  type="text" 
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="App Name..."
                  className="tech-input"
                  required
                />
              </div>

              <div>
                <label className="tech-label mb-2 block text-[#555]">Strategic Description</label>
                <textarea 
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  placeholder="What is this build solving?"
                  className="tech-input h-32 resize-none py-4"
                  required
                />
              </div>

              <div className="flex gap-4 pt-4">
                 <button 
                  type="button"
                  onClick={() => setEditingBuild(null)}
                  className="flex-1 py-4 border border-[#222] text-[#888] rounded-xl font-mono uppercase text-xs tracking-widest hover:bg-[#111] transition-all"
                >
                  Discard
                </button>
                <button 
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 py-4 bg-white text-black rounded-xl font-mono uppercase text-xs tracking-widest font-bold hover:bg-[#E0E0E0] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSaving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                  {isSaving ? 'Compiling Changes...' : 'Save Refinement'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}



